import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteQrCodeSettingsMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {DmsDataGrid, catchUnhandledDataGridActions, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['branch'] as const;

type BranchType = {
  id: string;
  label: string;
};

export const AccountingQrCodeSettings = () => {
  const navigate = useNavigate();
  const [deleteQrCodeSettings] = useDeleteQrCodeSettingsMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(id)) {
      return;
    }

    const branch = getColumnData<BranchType>('branch')?.value;

    match(actionKey)
      .with(Pattern.union('DETAIL', 'EDIT'), () =>
        navigate(composePath(settingsRoutes.qrCodeSettingsEdit, {params: {id}}))
      )
      .with('DELETE', () => {
        openDeleteDialog({
          text: i18n.t('page.accounting.actions.deleteQrSettings', {
            branchName: branch?.label ?? '',
          }),
          onConfirm: () => {
            deleteQrCodeSettings({id})
              .unwrap()
              .then(() => {
                refreshData();
              })
              .catch(handleApiError);
          },
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const title = i18n.t('page.accounting.labels.qrCodeSettingsTitle');

  const header: SettingsTemplateHeader = {
    title,
    actions: [
      {
        type: 'button',
        title: i18n.t('page.accounting.actions.addQrCodeSetting'),
        onClick: () => {
          navigate(settingsRoutes.qrCodeSettingsAdd);
        },
        'data-testid': testIds.settings.qrCodeSettings('createNew'),
      },
    ],
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={header}
        description={i18n.t('page.accounting.labels.qrCodeSettingsDescription')}
        data-testid={testIds.settings.qrCodeSettings('main')}
      >
        <DmsDataGrid
          gridCode="accounting-qr-code-settings"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.qrCodeSettings('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
};
